import { Box, CloseButton, Dialog, For, Portal, SimpleGrid, Spacer, Text } from '@chakra-ui/react'
import type { PropsOf } from '@emotion/react'
import sortBy from 'lodash/sortBy'
import type { FC, ReactNode } from 'react'
import { useMemo } from 'react'
import { useState } from 'react'

import { Button } from '@app/components/ui/button'
import AiPlaybooksModalAction from '@app/pages/playbooks/components/aiPlaybooksModalAction'
import AiPlaybooksModalCard from '@app/pages/playbooks/components/aiPlaybooksModalCard'
import AiPlaybooksModalTrigger from '@app/pages/playbooks/components/aiPlaybooksModalTrigger'
import type { PlaybooksQuery } from '@graphql/queries'

interface Props {
  parentStrategyId?: string
  showCTA?: boolean
  playbooks: PlaybooksQuery['playbooks']
  businessContextId?: string
  strategyId?: string
  children?: ReactNode
  Action?: FC<PropsOf<typeof AiPlaybooksModalAction>>
}

type CardPlaybook = PropsOf<typeof AiPlaybooksModalCard>['playbook']

const blankPlaybook: CardPlaybook = {
  id: 'blank',
  name: 'Blank canvas',
  shortDescription: 'Create a blank canvas'
}

const AiPlaybooksModal: FC<Props> = ({
  parentStrategyId,
  showCTA,
  playbooks,
  businessContextId = null,
  strategyId = null,
  children,
  Action
}) => {
  const [playbook, setPlaybook] = useState<CardPlaybook | null>(null)
  const [showInstructions, setShowInstructions] = useState(false)
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const sortedPlaybooks = useMemo(() => sortBy(playbooks, 'rowOrder'), [playbooks])
  const [loading, setLoading] = useState(false)

  const isPlaybookSelected = playbook && playbook.id !== 'blank'

  if (!showCTA) {
    return null
  }

  return (
    <Dialog.Root
      placement="center"
      size={showInstructions ? 'xl' : 'lg'}
      onExitComplete={() => {
        setPlaybook(null)
        setShowInstructions(false)
        setAdditionalInstructions('')
      }}
    >
      <Dialog.Trigger asChild>{children !== undefined ? children : <AiPlaybooksModalTrigger />}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Select a map type</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body maxH="450px" overflowY="hidden">
              <SimpleGrid columns={showInstructions ? 2 : 1} gap={4}>
                <SimpleGrid columns={showInstructions ? 1 : 2} gap={4} overflowY="auto" maxH="425px">
                  {!strategyId && (
                    <AiPlaybooksModalCard
                      playbook={blankPlaybook}
                      selectedPlaybook={playbook}
                      setSelectedPlaybook={setPlaybook}
                    />
                  )}
                  <For each={sortedPlaybooks} fallback={<Text>No AI Playbooks found</Text>}>
                    {(aiPlaybook) => (
                      <AiPlaybooksModalCard
                        key={aiPlaybook.id}
                        playbook={aiPlaybook}
                        selectedPlaybook={playbook}
                        setSelectedPlaybook={setPlaybook}
                      />
                    )}
                  </For>
                </SimpleGrid>
                {showInstructions && (
                  <Box maxH="400px">
                    <textarea
                      value={additionalInstructions}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                      style={{
                        width: '100%',
                        height: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #E2E8F0'
                      }}
                      disabled={loading}
                      placeholder="Add any additional context or instructions here..."
                    />
                  </Box>
                )}
              </SimpleGrid>
            </Dialog.Body>
            <Dialog.Footer>
              {isPlaybookSelected && (
                <Button
                  disabled={loading}
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowInstructions(!showInstructions)}
                >
                  + Custom Instructions
                </Button>
              )}
              <Spacer />
              {/* <Dialog.ActionTrigger asChild>
                <Button size="sm" variant="outline">
                  Cancel
                </Button>
              </Dialog.ActionTrigger> */}
              {Action ? (
                <Action
                  playbook={playbook}
                  strategyId={strategyId}
                  parentStrategyId={parentStrategyId}
                  additionalInstructions={additionalInstructions}
                  businessContextId={businessContextId}
                  onLoadingChange={setLoading}
                />
              ) : (
                <AiPlaybooksModalAction
                  playbook={playbook}
                  businessContextId={businessContextId}
                  additionalInstructions={additionalInstructions}
                  onLoadingChange={setLoading}
                />
              )}
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default AiPlaybooksModal
